import { useState, useEffect } from 'react';
import { graphql } from 'react-relay';
import { nanoid } from 'nanoid';
import Icon from '@/core/icon.js';
import { Markdown, useTranslation } from '@/helpers/translate';
import get from '@/helpers/getValue';
import { useZusModalStore, ModalWrapper } from '@/helpers/modal.js';
import { useSpacePublicZustandStore } from '@/helpers/spacepublicprovider';

const queryEquipments = graphql`
  fragment equipments_equipments on SpaceStandardServices @relay(plural: true) {
    slug
    items {
      slug
      score
    }
  }
`;

function EquipmentsModal({ equipments }) {
  const { t } = useTranslation();
  const [reactKey] = useState(nanoid());
  return (
    <section className="flex-col px-6 pt-10 pb-20 bg-white min-h-dvh lg:rounded-2xl lg:min-h-fit lg:h-fit md:p-12 text-purple-5/50 md:flex-row">
      <h2 className="w-full pb-4 border-white-dark2 m-0 font-heading text-[14px] font-extrabold uppercase leading-[1.2] tracking-[0.02em] text-was-ink md:text-[15px]">{t('space:equipments')}</h2>
      {equipments.map((e) => (
        <div className="pb-8" key={`equimentsmodal0${reactKey}${e.slug}`}>
          <h3 className="pb-4 text-left text-black">{t(`extra:category.${e.slug}`)}</h3>
          <div className="grid w-full grid-cols-2 gap-5 md:grid md:gap-5 md:grid-cols-3">
            {e.items.map((i) => (
              <div className="flex flex-row items-center md:mr-6" key={`equipmentsmodal1${reactKey}${e.slug}`}>
                <Icon name={i.slug} className="mr-3 fill-current text-purple-5/50 w-7 h-7" />
                <div className="flex flex-col text-sm leading-6 text-left text-purple-5/70">
                  {t(`extra:name.${i.slug}`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function EquimentsItems({ items, limit }) {
  const { t } = useTranslation();
  const [reactKey] = useState(nanoid());
  return (
    <>
      {items.slice(0, limit).map((e) => (
        <div className="flex flex-row items-center mr-6" key={`equipmentsitems${reactKey}${e.slug}`}>
          <Icon name={e.slug} className="mr-3 fill-current text-purple-5/80 size-6" />
          <div className="flex flex-col text-sm text-purple-5/60">
            {t(`extra:name.${e.slug}`)}
          </div>
        </div>
      ))}
    </>
  );
}

function Equipments({ equipments, limit = 6 }) {
  const { t } = useTranslation();
  if (!equipments) return null;
  const { setModal1 } = useZusModalStore.getState();
  const allEquipments = equipments.reduce((acc, cur) => {
    let newAcc = [...acc];
    newAcc = newAcc.concat(get(cur, 'items', []));
    return newAcc;
  }, []);
  allEquipments.sort((a, b) => -1 * (a.score - b.score));

  function onClickOpen() {
    setModal1(
      <ModalWrapper onClose={() => setModal1(null)} delay={1}>
        <EquipmentsModal {...{ equipments }} />
      </ModalWrapper>,
    );
  }

  return (
    <section className="flex-col mt-12 md:mt-12 text-purple-5/70 md:flex-row">
      <h2 className="w-full pb-5 border-t pt-9 border-white-dark2 md:pt-12 m-0 font-heading text-[14px] font-extrabold uppercase leading-[1.2] tracking-[0.02em] text-was-ink md:text-[15px]">{t('space:equipments')}</h2>
      <div className="grid w-full grid-cols-2 gap-4 overflow-hidden md:grid md:gap-6 md:grid-cols-3 md:overflow-auto">
        <EquimentsItems items={allEquipments} limit={limit} />
      </div>
      {allEquipments.length <= limit || <button className="px-3 py-2 mt-4 cursor-pointer frounded-lg text-purple-5 lg:mt-6 bg-purple-0 hover:bg-purple/10" onClick={onClickOpen}>
        <Markdown>{t('btn:see_more_equipments', { more: allEquipments.length })}
        </Markdown>
                                        </button>}
    </section>
  );
}

export {
  queryEquipments,
};

export default Equipments;
